import { useTranslation } from 'react-i18next'

import { useGetAnnouncementAnalytics } from '@/api/communication/announcements/get-announcement-analytics'
import { Typography } from '@/components/ui/typography'
import { type CommunicationType } from '@/models'
import { toFixed } from '@/utils/math'

import * as S from './announcement-details-analytics.styled'
import { isEmailType, isPushNotificationType } from './announcement-details-analytics.utils'

interface AnnouncementDetailsAnalyticsProps {
  id: number
  announcementType: CommunicationType
}

export const AnnouncementDetailsAnalytics = ({
  id,
  announcementType,
}: AnnouncementDetailsAnalyticsProps) => {
  const { t } = useTranslation(['communication'])
  const { data: analytics, isLoading } = useGetAnnouncementAnalytics({ id })

  const isPushNotification = isPushNotificationType(announcementType)
  const isEmailNotification = isEmailType(announcementType)
  const deliveryRate = analytics?.deliveryRate ?? 0

  return (
    <S.Card $isLoading={isLoading}>
      <S.MetricCard>
        <Typography size="text-small" weight="semibold" color="black800">
          {t('communication:announcementDetails.analytics.totalTargeted')}
        </Typography>
        <Typography size="heading-huge" color="black800">
          {analytics?.target ?? 0}
        </Typography>
      </S.MetricCard>

      <S.MetricCard>
        <Typography size="text-small" weight="semibold" color="black800">
          {t('communication:announcementDetails.analytics.totalSent')}
        </Typography>
        <Typography size="heading-huge" color="black800">
          {analytics?.sent ?? 0}
        </Typography>
      </S.MetricCard>

      {(isPushNotification || isEmailNotification) && (
        <S.MetricCard>
          <Typography size="text-small" weight="semibold" color="black800">
            {t('communication:announcementDetails.analytics.deliveryRate')}
          </Typography>
          <S.ValueWithUnit>
            <Typography as="span" color="black800" size="heading-huge">
              {toFixed(deliveryRate)}
            </Typography>
            <Typography as="span" color="black800" size="text-small" weight="semibold">
              %
            </Typography>
          </S.ValueWithUnit>
        </S.MetricCard>
      )}

      {isEmailNotification && (
        <>
          <S.MetricCard>
            <Typography size="text-small" weight="semibold" color="black800">
              {t('communication:announcementDetails.analytics.openRate')}
            </Typography>
            <S.ValueWithUnit>
              <Typography as="span" color="black800" size="heading-huge">
                {toFixed(analytics?.openRate)}
              </Typography>
              <Typography as="span" color="black800" size="text-small" weight="semibold">
                %
              </Typography>
            </S.ValueWithUnit>
          </S.MetricCard>

          <S.MetricCard>
            <Typography size="text-small" weight="semibold" color="black800">
              {t('communication:announcementDetails.analytics.clickRate')}
            </Typography>
            <S.ValueWithUnit>
              <Typography as="span" color="black800" size="heading-huge">
                {toFixed(analytics?.clickRate)}
              </Typography>
              <Typography as="span" color="black800" size="text-small" weight="semibold">
                %
              </Typography>
            </S.ValueWithUnit>
          </S.MetricCard>
        </>
      )}
    </S.Card>
  )
}
